import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { authFetch } from '../utils/apiHelper';
import { API_URL } from '../config';
import { getViewingModelId } from '../utils/shareSession';
import { qualityPos, QUALITY_POS } from '../utils/renderQuality';
import { isMobileDevice, isLowMemoryDevice, SOG_DECODE_MEMORY_MULTIPLIER, estimateAvailableMemoryBytes, autoTierFor, autoRenderer, autoRendererStreamed, requestedRenderer, hasWebGPUSupport, isStreamPerformanceOn, STREAM_PERFORMANCE_KEY } from '../Splat.ts';
import { modalPrimaryBtn, modalSecondaryBtn, MODAL_BTN_ROW, MODAL_BTN_HEIGHT } from '../modalButtons';
import { isJoystickEnabled, JOYSTICK_KEYS } from '../three/touchJoystick';
import { readCachedSpeed, fetchSpeed, saveSpeed, ZOOM_SPEED, MOVE_SPEED, LOOK_SPEED, SPEED_MIN, SPEED_MAX, SPEED_DEFAULT } from '../utils/speedPrefs';
import ModalOverlay from './ModalOverlay';
import { ADAPTIVE_KEY, isAdaptiveOn } from '../three/adaptiveQuality';

// Mirrors SplatLoader's constructor in Splat.ts exactly (the ?sh= absent
// branch) — on for desktop, off for phones, capped to degree 1 on
// iPad/low-memory devices.
const AUTO_SH_ON = !isMobileDevice;
const AUTO_SH_DEGREE = isLowMemoryDevice ? 1 : 3;


// Splat count slider range (%); SceneHandler accepts the same ?splats= range.
const SPLAT_PCT_MIN = 10;
const SPLAT_PCT_STEP = 5;
const fmtCount = (n) => n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n);


// When a model has no .splat (so no size/32 count), read the true splat count
// from whatever file IS there. Best-effort: any hiccup returns null and the
// count line just stays blank.

// SOG is a ZIP; meta.json (last entry, ~15KB) carries "count". Range-fetch
// the file's tail and parse it via the central directory.
async function peekSogCount(url, fileSize) {
  const tailLen = Math.min(fileSize, 262144);
  const res = await fetch(url, { headers: { Range: `bytes=${fileSize - tailLen}-${fileSize - 1}` } });
  if (!res.ok) return null;
  const buf = new Uint8Array(await res.arrayBuffer());
  const base = fileSize - buf.length;
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let eocd = -1;
  for (let i = buf.length - 22; i >= 0; i--) {
    if (buf[i] === 0x50 && buf[i + 1] === 0x4b && buf[i + 2] === 0x05 && buf[i + 3] === 0x06) { eocd = i; break; }
  }
  if (eocd < 0) return null;
  let cd = dv.getUint32(eocd + 16, true) - base;
  const cdEnd = cd + dv.getUint32(eocd + 12, true);
  if (cd < 0) return null;
  while (cd + 46 <= cdEnd && buf[cd] === 0x50 && buf[cd + 1] === 0x4b && buf[cd + 2] === 0x01 && buf[cd + 3] === 0x02) {
    const fnLen = dv.getUint16(cd + 28, true);
    const exLen = dv.getUint16(cd + 30, true);
    const cmLen = dv.getUint16(cd + 32, true);
    const name = new TextDecoder().decode(buf.subarray(cd + 46, cd + 46 + fnLen));
    if (name === 'meta.json') {
      const size = dv.getUint32(cd + 20, true);
      const lho = dv.getUint32(cd + 42, true) - base;
      if (lho < 0) return null;
      const start = lho + 30 + dv.getUint16(lho + 26, true) + dv.getUint16(lho + 28, true);
      const meta = JSON.parse(new TextDecoder().decode(buf.subarray(start, start + size)));
      return typeof meta.count === 'number' ? meta.count : null;
    }
    cd += 46 + fnLen + exLen + cmLen;
  }
  return null;
}

// PLY header ("element vertex N") sits at the very start.
async function peekPlyCount(url) {
  const res = await fetch(url, { headers: { Range: 'bytes=0-32767' } });
  if (!res.ok) return null;
  const head = new TextDecoder('latin1').decode(new Uint8Array(await res.arrayBuffer()));
  const m = head.match(/element\s+vertex\s+(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

async function readSplatCount(formats) {
  if (formats?.splat?.size) return Math.floor(formats.splat.size / 32);
  try {
    if (formats?.sog?.url && formats?.sog?.size) return await peekSogCount(formats.sog.url, formats.sog.size);
    if (formats?.ply?.url) return await peekPlyCount(formats.ply.url);
  } catch { /* fall through */ }
  return null;
}


// ?renderer= — '' means Auto (autoRenderer in Splat.ts, shared with
// SceneHandler.js). A manual pick applies on every device.
const RENDERER_OPTIONS = [
  { value: '', label: 'Auto' },
  { value: 'webgpu', label: 'WebGPU' },
  { value: 'webgl', label: 'WebGL' },
];

// ?navQuality= — resolution while the camera moves (SceneHandler's
// lower-resolution-while-moving), a 0.25–1 slider; '1' = Full (off).
// '' = Auto — by splat count (autoNavQuality in Splat.ts: < 5M Full,
// 5-10M 70%, > 10M 50%; phones 50%).

// ?resolution= — render resolution, one scale on every device (100% = 75%
// of native pixels, ~133% = every pixel);
// ?sharpness= — splat sharpness (covClamp) on the old preset scale. Both
// apply live; Resolution '' = Auto. Max 150%, or less where that would pass
// a pixel ratio of 3 (window.__resolutionMaxPct, set by SceneHandler).
const RESOLUTION_MIN = 25;
// Sharpness slider labels — a scale from softest to sharpest, not a quality
// rating; the default (High's covClamp) sits at 67.
const SHARPNESS_TICKS = [
  { pos: 0, label: 'Softest' },
  { pos: QUALITY_POS.high, label: 'Default' },
  { pos: 100, label: 'Sharpest' },
];
const resolutionMax = () => (typeof window !== 'undefined' && window.__resolutionMaxPct) || 150;

// Mirrors SceneHandler.js's ?sh= handling — '' means "no override" (device-
// based default: on for desktop, off for mobile, capped to 1 on phones/iPad).
// Best quality on the left, worst on the right — same direction as every
// other row in this modal: Auto, 3, 2, 1, Off.
const SH_OPTIONS = [
  { value: '', label: 'Auto' },
  { value: '3', label: '3' },
  { value: '2', label: '2' },
  { value: '1', label: '1' },
  { value: '0', label: 'Off' },
];

// Mirrors the SPLAT_FORMATS the backend checks in getModel, in its
// preference order — filtered down to whichever this specific model
// actually has uploaded (see fetchAvailableFormats below).
const ALL_FORMAT_OPTIONS = [
  { value: 'sog', label: 'SOG' },
  { value: 'ply', label: 'PLY' },
  { value: 'cply', label: 'CPLY' },
  { value: 'splat', label: 'Splat' },
];

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  display: 'block',
  marginBottom: 4,
};


// isAutoTarget: this is the concrete option Auto currently resolves to —
// gets an accent-colored ring (same #69edf9 used for selection elsewhere in
// the app, e.g. the captures page) regardless of active state, layered on
// top of the active/inactive fill via box-shadow so it doesn't affect layout.
const ACCENT = '#1a1a1a'; // the modal system's one accent color (active fills, Apply button, etc.)
const optionStyle = (active, isAutoTarget) => ({
  flex: 1,
  // Exactly the Apply/Cancel height (border included via border-box).
  height: MODAL_BTN_HEIGHT,
  boxSizing: 'border-box',
  padding: '0 4px',
  borderRadius: 7,
  border: active ? '1px solid #1a1a1a' : '1px solid #ddd',
  background: active ? '#1a1a1a' : 'transparent',
  color: active ? '#fff' : '#1a1a1a',
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: 'Inter, Arial, sans-serif',
  fontWeight: active ? 500 : 400,
  boxShadow: isAutoTarget ? `0 0 0 2px ${ACCENT}` : 'none',
});

// One short line under each setting's title.
// Compact Auto toggle next to a slider's value (optionStyle is sized for a
// row of equal buttons).
const autoPillStyle = { flex: '0 0 auto', width: 'auto', minWidth: 0, height: 'auto', minHeight: 0, padding: '4px 12px', fontSize: 12 };
// An Auto control while adaptive quality is switched off.
const autoOffStyle = { opacity: 0.4, cursor: 'default' };
const sliderHeadStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontFamily: 'Inter, Arial, sans-serif', marginBottom: 4 };
// Kept short enough for one line on a phone; nowrap + ellipsis as the
// guard if one ever isn't.
const captionStyle = {
  fontSize: 11,
  color: '#999',
  fontFamily: 'Inter, Arial, sans-serif',
  display: 'block',
  margin: '-2px 0 8px',
  lineHeight: 1.35,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const readParam = (key) => new URLSearchParams(window.location.search).get(key) || '';

// Listens for the 'Quality' item in the top-nav settings dropdown, shown
// only on the Scene/Viewer page (including shared-link viewers). Applying
// sets ?sh=/?format= etc. on the current URL and does a full reload —
// SceneHandler.js reads all three once at scene-init time, so a live
// in-place reconfiguration isn't safe (the loader, SH degree, and loaded
// file are all wired up together at construction).
// Streamed-SOG live-tuning sliders (dev route: ?stream=1). Unlike the
// options above, these apply instantly to the live octree via
// window.__splatOctree.setTuning — no reload — and only write the URL so a
// manual refresh keeps them.
// lodDist range covers everything from a tight interior (~20) to a city
// (~1500); the modal also stretches its max to at least the scene's
// auto-derived baseDistance so "max" is never below the sane default.
const STREAM_SLIDERS = [
  { key: 'budget', label: 'Budget (resident splats)', min: 250000, max: 10000000, step: 250000, fmt: (v) => v >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : `${Math.round(v / 1e3)}k` },
];

// Default background colour hex used when nothing overrides it — mirrors
// SceneHandler.js's own default exactly (kept here just for the colour
// picker's starting/reset value, not read by SceneHandler).
const DEFAULT_BG_HEX = '#20252b';

const QualityModal = () => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  // Sharpness (?sharpness=, a 0–100 position on the preset scale) — '' = default (High).
  const [sharpness, setSharpness] = useState('');
  const [resolution, setResolution] = useState('');
  // Resolution in use right now (window.__resolutionLivePct) — shown on Auto.
  const [liveResolution, setLiveResolution] = useState(100);
  const [liveDetail, setLiveDetail] = useState(100); // streamed: adaptive quality's detail lever, %
  const [sh, setSh] = useState('');
  const [renderer, setRenderer] = useState('');
  const [navQuality, setNavQuality] = useState('');
  // What Auto resolves to for the loaded model — set by SceneHandler once
  // it knows the splat count (window.__navQualityAuto).
  const [navQualityAuto, setNavQualityAuto] = useState('1');
  const [format, setFormat] = useState('');
  const [splats, setSplats] = useState('');
  const [splatTotal, setSplatTotal] = useState(null);
  // Static models: splat share drawn right now (adaptive quality moves it
  // live) and the share loaded — the most it can go to without a reload.
  const [liveSplatPct, setLiveSplatPct] = useState(100);
  // Spherical harmonics: degree drawn now (live) and the degree loaded.
  const [liveSh, setLiveSh] = useState(null);
  const [loadedSh, setLoadedSh] = useState(null);
  const [loadedSplatPct, setLoadedSplatPct] = useState(100);
  const [availableFormats, setAvailableFormats] = useState(null);
  const [autoFormat, setAutoFormat] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [stream, setStream] = useState({ budget: 2000000, lodDist: 36, lodMult: 2.6 });
  // Budget on Auto = adaptive quality manages it (no ?budget= in the URL).
  const [budgetAuto, setBudgetAuto] = useState(true);
  const [poolCapacity, setPoolCapacity] = useState(8000000);
  const [budgetSliderMax, setBudgetSliderMax] = useState(null);
  // Streamed model's full-detail splat count (window.__splatOctreeStats).
  const [streamModelSplats, setStreamModelSplats] = useState(null);
  const [debugHud, setDebugHud] = useState(false);
  // Manual per-model background colour override (General tab) — persisted
  // via the setBackgroundColor endpoint, applied by SceneHandler.js at scene
  // init (and live for a streaming session via window.__setSceneBackground).
  const [bgColorEnabled, setBgColorEnabled] = useState(false);
  const [bgColor, setBgColor] = useState(DEFAULT_BG_HEX);
  // Per-user zoom and move speeds (General tab, every user) — see
  // utils/speedPrefs.js.
  // Applied live to the scene while dragging; persisted on Apply; Cancel/✕
  // put the scene back to the saved value.
  const [zoomSpeed, setZoomSpeed] = useState(SPEED_DEFAULT);
  const [savedZoomSpeed, setSavedZoomSpeed] = useState(SPEED_DEFAULT);
  const zoomTouched = useRef(false);
  const [moveSpeed, setMoveSpeed] = useState(SPEED_DEFAULT);
  const [savedMoveSpeed, setSavedMoveSpeed] = useState(SPEED_DEFAULT);
  const moveTouched = useRef(false);
  // Look speed (turning / tilting: arrow keys, look joystick) — same as move.
  const [lookSpeed, setLookSpeed] = useState(SPEED_DEFAULT);
  const [savedLookSpeed, setSavedLookSpeed] = useState(SPEED_DEFAULT);
  const lookTouched = useRef(false);
  // On-screen touch joysticks (General tab, every user; default on for
  // phones, off for tablets / desktops) — move (left) and look (right), per browser (localStorage
  // JOYSTICK_KEYS), applied on Apply via
  // window.__setJoystickEnabled (SceneHandler.js).
  const [joystickOn, setJoystickOn] = useState(false);
  const [lookJoystickOn, setLookJoystickOn] = useState(false);
  // Streamed Performance switch (draw target) — applies live, saved per
  // browser on Apply, reverted on close.
  const [performanceOn, setPerformanceOn] = useState(false);
  // Adaptive quality switch — same pattern: live, saved on Apply, undone on close.
  const [adaptiveOn, setAdaptiveOn] = useState(true);
  const { hasRole } = useAuth();
  const isSysAdmin = hasRole('sysAdmin');

  // Gate the in-scene overlay on sysAdmin — SceneHandler only shows it when
  // this is true, so ?debugHud=1 / a manual localStorage edit does nothing
  // for anyone else.
  useEffect(() => {
    window.__debugHudAllowed = isSysAdmin;
    if (!isSysAdmin) {
      try { localStorage.removeItem('debugHud'); } catch (e) { /* noop */ }
    }
    window.dispatchEvent(new CustomEvent('debugHudToggle'));
  }, [isSysAdmin]);

  const toggleDebugHud = () => {
    if (!isSysAdmin) return;
    const next = !debugHud;
    setDebugHud(next);
    try { localStorage.setItem('debugHud', next ? '1' : '0'); } catch (e) { /* private mode */ }
    window.dispatchEvent(new CustomEvent('debugHudToggle', { detail: next }));
  };

  useEffect(() => {
    const handle = (e) => {
      if (e.detail !== 'quality') return;
      setResolution(readParam('resolution'));
      setSharpness(readParam('sharpness'));
      setLiveResolution(window.__resolutionLivePct ?? 100);
      setSh(readParam('sh'));
      setRenderer(requestedRenderer(new URLSearchParams(window.location.search)));
      setNavQuality(readParam('navQuality'));
      setNavQualityAuto(String(window.__navQualityAuto ?? 1));
      setFormat(readParam('format'));
      setSplats(readParam('splats'));
      setSplatTotal(null);
      setAvailableFormats(null);
      try { setDebugHud(localStorage.getItem('debugHud') === '1'); } catch (e) { setDebugHud(false); }
      setJoystickOn(isJoystickEnabled(JOYSTICK_KEYS.move));
      setLookJoystickOn(isJoystickEnabled(JOYSTICK_KEYS.look));
      setPerformanceOn(isStreamPerformanceOn());
      setAdaptiveOn(isAdaptiveOn());
      setActiveTab('general');
      const cachedZoom = readCachedSpeed(ZOOM_SPEED);
      setZoomSpeed(cachedZoom);
      setSavedZoomSpeed(cachedZoom);
      zoomTouched.current = false;
      // The DB value wins over the local copy — unless the slider has
      // already been moved by the time it arrives.
      fetchSpeed(ZOOM_SPEED).then((v) => {
        if (v == null) return;
        setSavedZoomSpeed(v);
        if (!zoomTouched.current) setZoomSpeed(v);
      });
      const cachedMove = readCachedSpeed(MOVE_SPEED);
      setMoveSpeed(cachedMove);
      setSavedMoveSpeed(cachedMove);
      moveTouched.current = false;
      fetchSpeed(MOVE_SPEED).then((v) => {
        if (v == null) return;
        setSavedMoveSpeed(v);
        if (!moveTouched.current) setMoveSpeed(v);
      });
      const cachedLook = readCachedSpeed(LOOK_SPEED);
      setLookSpeed(cachedLook);
      setSavedLookSpeed(cachedLook);
      lookTouched.current = false;
      fetchSpeed(LOOK_SPEED).then((v) => {
        if (v == null) return;
        setSavedLookSpeed(v);
        if (!lookTouched.current) setLookSpeed(v);
      });
      setBgColorEnabled(false);
      setBgColor(DEFAULT_BG_HEX);
      setOpen(true);

      // Streamed-SOG — show the streaming controls whenever a live octree
      // exists, whether reached via the ?stream=1 dev route or a real
      // ?modelId= streamed model (SceneHandler routes on modelData.lodMetaUrl;
      // the octree sets window.__splatOctree in its constructor either way).
      // Seed the sliders from whatever the live octree currently resolved to
      // (folds in ?budget=/?lodDist=/?lodMult= and the per-scene auto values).
      const streaming = new URLSearchParams(window.location.search).get('stream') === '1'
        || !!window.__splatOctree || !!window.__streamedModel;
      setIsStreaming(streaming);
      if (streaming) {
        const s = window.__splatOctreeStats || {};
        setBudgetAuto(!readParam('budget'));
        setStream({
          budget: s.budget || parseInt(readParam('budget'), 10) || 2000000,
          lodDist: s.baseDistance || parseFloat(readParam('lodDist')) || 36,
          lodMult: s.lodMultiplier || parseFloat(readParam('lodMult')) || 2.6,
        });
        // Slider max = the largest budget allowed (the pool itself has a
        // little extra room as swap headroom — not offered as budget).
        if (s.budgetMax || s.poolCapacity) setPoolCapacity(s.budgetMax || s.poolCapacity);
        // Slider goes up to the device max (capped at the model's size);
        // above the current pool, Apply reloads with that ?budget=.
        const deviceMax = window.__streamDeviceMaxBudget;
        const modelMax = s.modelSplats || Infinity;
        setBudgetSliderMax(Math.min(Number.isFinite(deviceMax) ? deviceMax : Infinity, modelMax, 10000000 * 4));
        setStreamModelSplats(s.modelSplats || null);
      }

      const modelId = getViewingModelId();
      if (modelId) {
        authFetch(`${API_URL}?getModel=true&id=${modelId}`)
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            // A streamed model (it has an LOD tree) — even when the menu is
            // opened before the octree exists (still loading): no File format
            // choice for those, there's only the streamed SOG.
            if (data?.lodMetaUrl) setIsStreaming(true);
            const formats = data?.formats ? Object.keys(data.formats) : [];
            setAvailableFormats(formats);

            // Splat count for the 100/75/50 options — .splat size/32 if it
            // exists, otherwise read it out of the SOG/PLY that does.
            readSplatCount(data?.formats).then((c) => setSplatTotal(c || null)).catch(() => {});

            // Mirrors resolveSplatUrl's proactive memory check in
            // SceneHandler.js exactly: the backend's defaultFormat is just
            // the quality-order winner (sog > ply > cply > splat) with no
            // idea of this device's memory — on a device where decoding the
            // sog would likely exceed it, the actual load silently falls
            // back to splat. Without replicating that here, this modal told
            // devices "Auto → SOG" while the model name bar (driven by the
            // real load) correctly showed "(splat)" — conflicting info.
            let resolvedFormat = data?.defaultFormat || '';
            const sogSize = data?.formats?.sog?.size;
            const splatAvailable = !!data?.formats?.splat?.url;
            if (resolvedFormat === 'sog' && splatAvailable && sogSize) {
              const estimatedDecodedBytes = sogSize * SOG_DECODE_MEMORY_MULTIPLIER;
              const availableBytes = estimateAvailableMemoryBytes();
              const SAFETY_MARGIN = 0.5;
              if (estimatedDecodedBytes > availableBytes * SAFETY_MARGIN) {
                resolvedFormat = 'splat';
              }
            }
            setAutoFormat(resolvedFormat);

            // A stale ?format= param for a format this model no longer has
            // (or never had) — fall back to Auto rather than keeping an
            // unselectable value around.
            setFormat(prev => (prev && !formats.includes(prev) ? '' : prev));
          })
          .catch(() => setAvailableFormats([]));

        // Same endpoint SceneHandler.js reads at scene init — General tab's
        // background-colour section just mirrors whatever's actually stored.
        authFetch(`${API_URL}?id=${modelId}&defaultPosition=true`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
        })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data?.BackgroundColorEnabled) setBgColorEnabled(true);
            if (data?.BackgroundColor) setBgColor(data.BackgroundColor);
          })
          .catch(() => {});
      } else {
        setAvailableFormats([]);
      }
    };
    window.addEventListener('dashboardNav', handle);
    return () => window.removeEventListener('dashboardNav', handle);
  }, []);

  const formatOptions = [
    { value: '', label: 'Auto' },
    ...ALL_FORMAT_OPTIONS.filter(opt => availableFormats?.includes(opt.value)),
  ];

  // The concrete value each section's "Auto" resolves to right now — used to
  // ring that option. Quality + splat count follow the size-based phone tier
  // (autoTierFor), so they depend on this model's splat count.
  const autoTier = autoTierFor(splatTotal || 0);
  // On Auto the outline marks the degree drawn right now (adaptive quality
  // may have lowered it), once the model is loaded.
  const autoShTarget = liveSh != null ? String(liveSh) : (AUTO_SH_ON ? String(AUTO_SH_DEGREE) : '0');
  // Up to the loaded degree it's live; more reloads on Apply.
  const shIsLive = (v) => !v || (loadedSh != null && Number(v) <= loadedSh);
  // Adaptive quality switch. Off: adaptive stops and its settings go back to
  // their defaults (SceneHandler), the Auto buttons below are off and greyed.
  // On: every Quality setting goes back to Auto — manual picks included — and
  // adaptive quality takes them over again.
  const setAdaptive = (on) => {
    setAdaptiveOn(on);
    if (on) {
      setResolution(''); window.__setResolution?.(null);
      setNavQuality(''); window.__setNavQuality?.(null);
      setSh(''); window.__shLive?.setAuto(true);
      if (!isStreaming) { setSplats(''); window.__splatShare?.setAuto(true); }
    }
    window.__setAdaptiveEnabled?.(on);
  };

  const pickSh = (v) => {
    setSh(v);
    if (v === '') window.__shLive?.setAuto(true);
    else if (shIsLive(v)) window.__shLive?.set(Number(v));
  };

  // .splat files don't carry SH data at all (only PLY/SOG/CPLY do) — once
  // it's clear that's what will actually load, whether picked explicitly or
  // via Auto, an SH choice would just be a dead setting.
  const effectiveFormat = format || autoFormat;
  const isSplatFormat = effectiveFormat === 'splat';
  const shDisabled = isSplatFormat;
  // WebGPU isn't offered where the browser has no navigator.gpu.
  const webgpuUnavailable = !hasWebGPUSupport ? 'Not available in this browser' : null;

  const autoSplatTarget = String(autoTier.splats);
  const splatCountFor = (v) => {
    if (!splatTotal) return null;
    const pct = v === '' ? Number(autoSplatTarget) : Number(v);
    return Math.floor(splatTotal * pct / 100);
  };

  useEffect(() => {
    if (open) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  // Live-apply a streaming slider to the octree as it moves — no reload.
  const applyStreamLive = (next) => {
    setStream(next);
    window.__splatOctree?.setTuning?.({ budget: next.budget, lodDist: next.lodDist, lodMult: next.lodMult });
  };

  useEffect(() => {
    const onChange = (e) => {
      setLiveResolution(window.__resolutionLivePct ?? 100);
      // Adaptive quality moves the streaming budget in the same step.
      setNavQualityAuto(String(window.__navQualityAuto ?? 1));
      const b = window.__splatOctreeStats?.budget;
      if (b) setStream((st) => (st.budget === b ? st : { ...st, budget: b }));
      setLiveDetail(window.__splatOctree?.detailPct ?? 100);
      if (window.__splatShare) {
        setLiveSplatPct(window.__splatShare.pct);
        setLoadedSplatPct(window.__splatShare.loaded);
      }
      if (window.__shLive) {
        setLiveSh(window.__shLive.degree);
        setLoadedSh(window.__shLive.loaded);
      }
    };
    onChange({});
    window.addEventListener('renderQualityChange', onChange);
    window.addEventListener('adaptiveQualityChange', onChange);
    window.addEventListener('splatShareChange', onChange);
    window.addEventListener('shDegreeChange', onChange);
    return () => {
      window.removeEventListener('shDegreeChange', onChange);
      window.removeEventListener('renderQualityChange', onChange);
      window.removeEventListener('adaptiveQualityChange', onChange);
      window.removeEventListener('splatShareChange', onChange);
    };
  }, [open]);



  const changeZoomSpeed = (v) => {
    zoomTouched.current = true;
    setZoomSpeed(v);
    window.__setZoomSpeed?.(v);
  };

  const changeLookSpeed = (v) => {
    lookTouched.current = true;
    setLookSpeed(v);
    window.__setLookSpeed?.(v);
  };

  const changeMoveSpeed = (v) => {
    moveTouched.current = true;
    setMoveSpeed(v);
    window.__setMoveSpeed?.(v);
  };

  const closeModal = () => {
    window.__setZoomSpeed?.(savedZoomSpeed);
    window.__setMoveSpeed?.(savedMoveSpeed);
    window.__setLookSpeed?.(savedLookSpeed);
    window.__setStreamPerformance?.(isStreamPerformanceOn());
    window.__setAdaptiveEnabled?.(isAdaptiveOn());
    setOpen(false);
  };

  const handleApply = async () => {
    // Save only a changed choice, so an untouched one keeps following the
    // device default.
    [[JOYSTICK_KEYS.move, joystickOn], [JOYSTICK_KEYS.look, lookJoystickOn]].forEach(([key, on]) => {
      if (on === isJoystickEnabled(key)) return;
      try { localStorage.setItem(key, on ? '1' : '0'); } catch (e) { /* storage off */ }
    });
    window.__setJoystickEnabled?.('move', joystickOn);
    window.__setJoystickEnabled?.('look', lookJoystickOn);
    try { localStorage.setItem(STREAM_PERFORMANCE_KEY, performanceOn ? '1' : '0'); } catch (e) { /* storage off */ }
    try { localStorage.setItem(ADAPTIVE_KEY, adaptiveOn ? '1' : '0'); } catch (e) { /* storage off */ }

    // One write at a time: each is a separate update of the same item.
    if (zoomSpeed !== savedZoomSpeed) {
      const saved = await saveSpeed(ZOOM_SPEED, zoomSpeed);
      setSavedZoomSpeed(saved);
      window.__setZoomSpeed?.(saved);
    }
    if (moveSpeed !== savedMoveSpeed) {
      const saved = await saveSpeed(MOVE_SPEED, moveSpeed);
      setSavedMoveSpeed(saved);
      window.__setMoveSpeed?.(saved);
    }
    if (lookSpeed !== savedLookSpeed) {
      const saved = await saveSpeed(LOOK_SPEED, lookSpeed);
      setSavedLookSpeed(saved);
      window.__setLookSpeed?.(saved);
    }

    // Background colour: sysAdmin-only for now (see the General tab's own
    // comment) — persist it, then apply immediately so a streaming session
    // (which may not reload below) doesn't need one just to see it. Awaited
    // before the reload paths below so the write isn't cut off by
    // navigation; best-effort otherwise — a failed save shouldn't block the
    // rest of Apply.
    const modelId = getViewingModelId();
    if (modelId && isSysAdmin) {
      try {
        await authFetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
          body: JSON.stringify({ setBackgroundColor: { modelId, enabled: bgColorEnabled, color: bgColor } }),
        });
      } catch (e) { /* best-effort */ }
      window.__setSceneBackground?.(bgColorEnabled ? bgColor : null);
    }

    const params = new URLSearchParams(window.location.search);
    const setOrDelete = (key, value) => {
      if (value) params.set(key, value);
      else params.delete(key);
    };
    setOrDelete('resolution', resolution);
    setOrDelete('sharpness', sharpness);
    setOrDelete('sh', isSplatFormat ? '' : sh);
    // Legacy ?webgpu= is folded into ?renderer= (see requestedRenderer).
    params.delete('webgpu');
    setOrDelete('renderer', renderer);
    // Navigation quality applies live — never needs a reload.
    params.delete('motionScale');
    setOrDelete('navQuality', navQuality);
    window.__setNavQuality?.(navQuality ? parseFloat(navQuality) : null);
    setOrDelete('format', format);
    setOrDelete('splats', splats);

    if (isStreaming) {
      // Only a manual budget goes in the URL — on Auto, adaptive quality
      // manages it (a ?budget= would pin it on the next load).
      if (budgetAuto) params.delete('budget');
      else params.set('budget', String(Math.round(stream.budget)));
      // Detail distance / LOD falloff aren't in the menu any more (the
      // budget decides what's loaded) — automatic per model; ?lodDist= /
      // ?lodMult= still work when put in the address by hand.
      // Sliders, render quality and SH up to the loaded degree are already
      // applied live. Only an SH above what's loaded
      // needs a reload (pool textures + decode workers are sized at load).
      const shChanged = (sh || '') !== (readParam('sh') || '') && !shIsLive(sh);
      const rendererChanged = renderer !== requestedRenderer(new URLSearchParams(window.location.search));
      // The pool is sized at load to the starting budget — more needs a reload.
      const budgetAbovePool = !budgetAuto && stream.budget > poolCapacity;
      if (shChanged || rendererChanged || budgetAbovePool) {
        window.location.href = `${window.location.pathname}?${params.toString()}`;
        return;
      }
      window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
      setOpen(false);
      return;
    }

    const query = params.toString();
    // Nothing that needs a reload changed (e.g. only zoom speed, navigation
    // quality, or the live-applied background colour) — update the URL and
    // close.
    // Splat share up to what's loaded is live too (only more needs a reload).
    const splatsLive = !splats || Number(splats) <= loadedSplatPct;
    const withoutLive = (p) => {
      const c = new URLSearchParams(p);
      c.delete('navQuality'); c.delete('motionScale'); c.delete('resolution'); c.delete('sharpness');
      if (splatsLive) c.delete('splats');
      if (shIsLive(sh)) c.delete('sh');
      return c.toString();
    };
    if (withoutLive(params) === withoutLive(window.location.search)) {
      window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      setOpen(false);
      return;
    }
    // Full reload, not client-side navigation — every one of these params
    // is only ever read once, at scene-init time.
    window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
  };

  if (!open) return null;

  return (
    <ModalOverlay>
      <div
        className="modal-content"
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          padding: '28px 30px 24px',
          maxWidth: 420,
          width: '90%',
          pointerEvents: 'auto',
          textAlign: 'left',
          // Header, tabs and buttons stay put; the settings body scrolls when
          // it's taller than the screen (it used to overflow off-screen with
          // no way to reach the lower options or the buttons). 100% = the
          // overlay's visible area inside its safe-area padding.
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'Inter, Arial, sans-serif', color: '#1a1a1a' }}>
            Settings
          </span>
          <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999', lineHeight: 1 }}>✕</button>
        </div>

        {/* General, Quality and Controls for everyone (General opens first); Admin (background colour,
            DEBUG) for sysAdmin only. Quality also carries the streamed-only
            level-of-detail sliders; General: Renderer, File format;
            Controls: zoom / move speed, joysticks. */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #eee' }}>
          {[{ key: 'general', label: 'General' }, { key: 'quality', label: 'Quality' }, { key: 'controls', label: 'Controls' }, ...(isSysAdmin ? [{ key: 'admin', label: 'Admin' }] : [])].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              style={{
                flex: 1, padding: '8px 0 10px', background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: activeTab === t.key ? '2px solid #1a1a1a' : '2px solid transparent',
                marginBottom: -1,
                color: activeTab === t.key ? '#1a1a1a' : '#888',
                fontWeight: activeTab === t.key ? 600 : 400,
                fontSize: 13, fontFamily: 'Inter, Arial, sans-serif',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, margin: '0 -30px', padding: '0 30px' }}>
        {/* Visual trade-offs. Titles only — the outline on an option marks
            what Auto currently resolves to. */}
        {activeTab === 'quality' && (
        <>
        <label style={sectionLabelStyle}>Adaptive quality</label>
        <span style={captionStyle}>{"Lowers the settings below when the scene gets slow."}</span>
        <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
          {[{ on: true, label: 'On' }, { on: false, label: 'Off' }].map((opt) => (
            <button key={opt.label} type="button" style={optionStyle(adaptiveOn === opt.on, false)}
              onClick={() => setAdaptive(opt.on)}>
              {opt.label}
            </button>
          ))}
        </div>

        <label style={sectionLabelStyle}>Resolution</label>
        <span style={captionStyle}>{"Lower is faster. About 133% is every screen pixel."}</span>
        {(() => {
          const auto = resolution === '';
          const max = resolutionMax();
          const pct = auto ? liveResolution : Number(resolution);
          const pick = (v) => { setResolution(String(v)); window.__setResolution?.(v); };
          return (
            <div style={{ marginBottom: 18 }}>
              <div style={sliderHeadStyle}>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: '#555' }}>{`${pct}%`}</span>
                <button type="button" disabled={!adaptiveOn} style={{ ...optionStyle(auto && adaptiveOn, false), ...autoPillStyle, ...(adaptiveOn ? null : autoOffStyle) }}
                  onClick={() => {
                    if (auto) pick(Math.max(RESOLUTION_MIN, Math.min(max, pct)));
                    else { setResolution(''); window.__setResolution?.(null); }
                  }}>Auto</button>
              </div>
              <input type="range" min={RESOLUTION_MIN} max={max} step={5} value={Math.max(RESOLUTION_MIN, Math.min(max, pct))}
                onChange={(e) => pick(parseInt(e.target.value, 10))}
                style={{ width: '100%', accentColor: '#1a1a1a', cursor: 'pointer' }} />
            </div>
          );
        })()}

        <label style={sectionLabelStyle}>Navigation quality</label>
        <span style={captionStyle}>{"Resolution while the camera moves."}</span>
        {(() => {
          const auto = navQuality === '';
          const pct = Math.round((auto ? parseFloat(navQualityAuto) || 1 : parseFloat(navQuality)) * 100);
          return (
            <div style={{ marginBottom: 18 }}>
              <div style={sliderHeadStyle}>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: '#555' }}>{pct >= 100 ? 'Full' : `${pct}%`}</span>
                <button type="button" disabled={!adaptiveOn} style={{ ...optionStyle(auto && adaptiveOn, false), ...autoPillStyle, ...(adaptiveOn ? null : autoOffStyle) }}
                  onClick={() => {
                    if (auto) { setNavQuality(String(pct / 100)); window.__setNavQuality?.(pct / 100); }
                    else { setNavQuality(''); window.__setNavQuality?.(null); }
                  }}>Auto</button>
              </div>
              <input type="range" min={25} max={100} step={5} value={pct}
                onChange={(e) => { const v = parseInt(e.target.value, 10) / 100; setNavQuality(String(v)); window.__setNavQuality?.(v); }}
                style={{ width: '100%', accentColor: '#1a1a1a', cursor: 'pointer' }} />
            </div>
          );
        })()}

        {!isStreaming && (
        <>
        <label style={sectionLabelStyle}>Splat count</label>
        <span style={captionStyle}>{"Share of the model's splats drawn."}</span>
        {(() => {
          // Up to what's loaded it applies live (no reload); above that it
          // needs Apply, which reloads the model with more splats.
          const auto = splats === '';
          const pct = auto ? liveSplatPct : Number(splats);
          const n = splatCountFor(String(pct));
          const needsReload = pct > loadedSplatPct;
          const pick = (v) => {
            setSplats(String(v));
            if (v <= loadedSplatPct) window.__splatShare?.set(v);
          };
          return (
            <div style={{ marginBottom: 18 }}>
              <div style={sliderHeadStyle}>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: '#555' }}>
                  {pct}%{n ? ` · ${fmtCount(n)} splats` : ''}{needsReload ? ' · reloads on Apply' : ''}
                </span>
                <button type="button" disabled={!adaptiveOn} style={{ ...optionStyle(auto && adaptiveOn, false), ...autoPillStyle, ...(adaptiveOn ? null : autoOffStyle) }}
                  onClick={() => {
                    if (auto) { setSplats(String(pct)); window.__splatShare?.setAuto(false); }
                    else { setSplats(''); window.__splatShare?.setAuto(true); }
                  }}>Auto</button>
              </div>
              <input type="range" min={SPLAT_PCT_MIN} max={100} step={SPLAT_PCT_STEP} value={pct}
                onChange={(e) => pick(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#1a1a1a', cursor: 'pointer' }} />
            </div>
          );
        })()}
        </>
        )}

        <label style={sectionLabelStyle}>Spherical harmonics</label>
        <span style={captionStyle}>{"Reflections and shine. Higher is slower."}</span>
        {isStreaming ? (
            <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
            {SH_OPTIONS.map((opt) => (
              <button key={opt.value} type="button" onClick={() => pickSh(opt.value)}
                disabled={opt.value === '' && !adaptiveOn}
                style={{ ...optionStyle(sh === opt.value && (opt.value !== '' || adaptiveOn), sh === '' && opt.value === autoShTarget), ...(opt.value === '' && !adaptiveOn ? autoOffStyle : null) }}>
                {opt.label}
              </button>
            ))}
          </div>
        ) : (
        <div style={{ display: 'flex', gap: 6, marginBottom: 24, opacity: shDisabled ? 0.4 : 1, pointerEvents: shDisabled ? 'none' : 'auto' }}>
          {SH_OPTIONS.map((opt) => (
            <button key={opt.value} type="button" onClick={() => pickSh(opt.value)} disabled={shDisabled || (opt.value === '' && !adaptiveOn)} style={{ ...optionStyle(!shDisabled && sh === opt.value && (opt.value !== '' || adaptiveOn), !shDisabled && sh === '' && opt.value === autoShTarget), ...(opt.value === '' && !adaptiveOn ? autoOffStyle : null) }}>
              {opt.label}
            </button>
          ))}
        </div>
        )}

        </>
        )}

        {activeTab === 'general' && (
          <>
        <label style={sectionLabelStyle}>Renderer</label>
        <span style={captionStyle}>{"Graphics engine that draws the scene."}</span>
        <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
          {RENDERER_OPTIONS.map((opt) => {
            const disabled = opt.value === 'webgpu' && !!webgpuUnavailable;
            return (
              <button key={opt.value} type="button" onClick={() => setRenderer(opt.value)} disabled={disabled}
                style={{ ...optionStyle(renderer === opt.value, renderer === '' && opt.value === (isStreaming ? autoRendererStreamed : autoRenderer)), ...(disabled ? { opacity: 0.4, cursor: 'default' } : null) }}>
                {opt.label}
              </button>
            );
          })}
        </div>

        {!isStreaming && (
        <>
        <label style={sectionLabelStyle}>File format</label>
        <span style={captionStyle}>{"Which uploaded version is loaded."}</span>
        {/* Skeleton buttons while the format list loads — identical box model
            to the real ones, so the section height never changes. */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
          {availableFormats === null
            ? [0, 1, 2].map((i) => (
                <button key={i} type="button" disabled style={{ ...optionStyle(false, false), color: 'transparent', cursor: 'default' }}>·</button>
              ))
            : formatOptions.map((opt) => (
                <button key={opt.value} type="button" onClick={() => setFormat(opt.value)} style={optionStyle(format === opt.value, format === '' && opt.value !== '' && opt.value === autoFormat)}>
                  {opt.label}
                </button>
              ))}
        </div>
        </>
        )}

        {/* Not touched by adaptive quality (those stay together on the
            Quality tab): sharpness, and for streamed models the
            Performance switch and the streaming budget. */}
        <label style={sectionLabelStyle}>Sharpness</label>
        <span style={captionStyle}>{"How crisp each splat is drawn."}</span>
        {(() => {
          // No Auto — nothing adjusts sharpness; the default is High, and
          // High is kept out of the address ('' = default).
          const pos = qualityPos(sharpness) ?? QUALITY_POS.high;
          const pick = (v) => { setSharpness(v === QUALITY_POS.high ? '' : String(v)); window.__setSharpness?.(v); };
          return (
            <div style={{ marginBottom: 18 }}>
              <div style={sliderHeadStyle}>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: '#555' }}>{SHARPNESS_TICKS.find((t) => t.pos === pos)?.label ?? pos}</span>
              </div>
              <input type="range" min={0} max={100} step={1} value={pos}
                onChange={(e) => pick(parseInt(e.target.value, 10))}
                style={{ width: '100%', accentColor: '#1a1a1a', cursor: 'pointer' }} />
              <div style={{ position: 'relative', height: 14 }}>
                {SHARPNESS_TICKS.map((t) => (
                  <span key={t.pos} style={{ position: 'absolute', left: `${t.pos}%`, transform: `translateX(-${t.pos}%)`, fontSize: 11, color: '#888', fontFamily: 'Inter, Arial, sans-serif' }}>{t.label}</span>
                ))}
              </div>
            </div>
          );
        })()}

        {isStreaming && (
          <>
            <label style={sectionLabelStyle}>Performance</label>
            <span style={captionStyle}>{"Draws fewer splats for speed. Distant areas can show gaps."}</span>
            <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
              {[{ on: false, label: 'Off' }, { on: true, label: 'On' }].map((opt) => (
                <button key={opt.label} type="button" style={optionStyle(performanceOn === opt.on, false)}
                  onClick={() => { setPerformanceOn(opt.on); window.__setStreamPerformance?.(opt.on); }}>
                  {opt.label}
                </button>
              ))}
            </div>

            <label style={sectionLabelStyle}>Streaming budget</label>
            <span style={captionStyle}>
              {"Splats kept loaded"}
              {streamModelSplats ? `, out of ${fmtCount(streamModelSplats)} in the full model` : ''}{"."}
            </span>
            {(() => {
              const { min, max, step, fmt } = STREAM_SLIDERS[0];
              const sliderMax = budgetSliderMax || poolCapacity || max;
              // On Auto, adaptive quality may lower detail, which uses only
              // that share of the budget: show what's in effect, like the
              // Resolution slider shows its live value.
              const lowered = budgetAuto && liveDetail < 100;
              const value = Math.max(min, Math.min(sliderMax, lowered ? stream.budget * liveDetail / 100 : stream.budget));
              const needsReload = !budgetAuto && poolCapacity && value > poolCapacity;
              return (
                <div style={{ marginBottom: 14 }}>
                  <div style={sliderHeadStyle}>
                    <span style={{ fontVariantNumeric: 'tabular-nums', color: '#555' }}>{fmt(value)} splats</span>
                    <button type="button" style={{ ...optionStyle(budgetAuto, false), ...autoPillStyle }}
                      onClick={() => {
                        const next = !budgetAuto;
                        setBudgetAuto(next);
                        const b = window.__setStreamBudgetAuto?.(next);
                        if (b) setStream((st) => ({ ...st, budget: b }));
                      }}>Auto</button>
                  </div>
                  <input
                    type="range"
                    min={min}
                    max={sliderMax}
                    step={step}
                    value={value}
                    onChange={(e) => { if (budgetAuto) window.__setStreamBudgetAuto?.(false); setBudgetAuto(false); applyStreamLive({ ...stream, budget: parseFloat(e.target.value) }); }}
                    style={{ width: '100%', accentColor: '#1a1a1a', cursor: 'pointer' }}
                  />
                  {needsReload && (
                    <span style={{ ...captionStyle, margin: '4px 0 0' }}>{`Above ${fmt(poolCapacity)} — Apply reloads the scene.`}</span>
                  )}
                </div>
              );
            })()}
            <div style={{ height: 8 }} />
          </>
        )}
          </>
        )}

        {/* Speeds and on-screen joysticks. */}
        {activeTab === 'controls' && (
          <>
              {[
                { label: 'Zoom speed', caption: 'Scroll wheel and pinch zoom speed.', value: zoomSpeed, change: changeZoomSpeed },
                { label: 'Move speed', caption: 'Moving: W/A/S/D keys and the move joystick.', value: moveSpeed, change: changeMoveSpeed },
                { label: 'Look speed', caption: 'Looking around: arrow keys and the look joystick.', value: lookSpeed, change: changeLookSpeed },
              ].map((row) => (
                <React.Fragment key={row.label}>
                  <label style={sectionLabelStyle}>{row.label}</label>
                  <span style={captionStyle}>{row.caption}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
                    {/* Log2 scale so 0.5× and 2× sit equally far from the
                        default in the middle. */}
                    <input
                      type="range"
                      min={Math.log2(SPEED_MIN)}
                      max={Math.log2(SPEED_MAX)}
                      step={0.05}
                      value={Math.log2(row.value)}
                      onChange={(e) => row.change(Math.round(2 ** parseFloat(e.target.value) * 100) / 100)}
                      style={{ flex: 1, accentColor: '#1a1a1a', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 12, color: '#555', fontFamily: 'Inter, Arial, sans-serif', fontVariantNumeric: 'tabular-nums', width: 40, textAlign: 'right' }}>
                      {row.value.toFixed(2)}×
                    </span>
                    <button
                      type="button"
                      onClick={() => row.change(SPEED_DEFAULT)}
                      disabled={row.value === SPEED_DEFAULT}
                      style={{
                        background: 'none', border: 'none', padding: 0, fontSize: 12, fontFamily: 'Inter, Arial, sans-serif',
                        color: '#1a1a1a', textDecoration: 'underline',
                        cursor: row.value === SPEED_DEFAULT ? 'default' : 'pointer',
                        opacity: row.value === SPEED_DEFAULT ? 0.3 : 1,
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </React.Fragment>
              ))}

              {[
                { label: 'Move joystick', caption: 'Bottom-left joystick for walking.', on: joystickOn, set: setJoystickOn },
                { label: 'Look joystick', caption: 'Bottom-right joystick for looking around.', on: lookJoystickOn, set: setLookJoystickOn },
              ].map((row) => (
                <React.Fragment key={row.label}>
                  <label style={sectionLabelStyle}>{row.label}</label>
                  <span style={captionStyle}>{row.caption}</span>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 22 }}>
                    {[{ value: false, label: 'Off' }, { value: true, label: 'On' }].map((opt) => (
                      <button key={opt.label} type="button" onClick={() => row.set(opt.value)} style={optionStyle(row.on === opt.value, false)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </React.Fragment>
              ))}

          </>
        )}

        {/* sysAdmin only (tab hidden for everyone else). */}
        {activeTab === 'admin' && isSysAdmin && (
          <>
              {/* sysAdmin-only for now — the backend enforces canEdit either
                  way, but the UI stays simple until there's a real need for
                  per-model-admin (not just sysAdmin) access to this. */}
              <label style={sectionLabelStyle}>Background colour</label>
              <span style={captionStyle}>{"Backdrop colour behind the model."}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
                <button
                  type="button"
                  onClick={() => setBgColorEnabled(!bgColorEnabled)}
                  style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}
                >
                  <span style={{
                    width: 34, height: 20, borderRadius: 10, flexShrink: 0, position: 'relative',
                    background: bgColorEnabled ? '#1a1a1a' : '#ccc', transition: 'background 0.15s',
                  }}>
                    <span style={{
                      position: 'absolute', top: 2, left: bgColorEnabled ? 16 : 2, width: 16, height: 16,
                      borderRadius: '50%', background: '#fff', transition: 'left 0.15s',
                    }} />
                  </span>
                </button>
                <input
                  type="color"
                  value={bgColor}
                  disabled={!bgColorEnabled}
                  onChange={(e) => setBgColor(e.target.value)}
                  style={{
                    width: 40, height: 28, border: '1px solid #ddd', borderRadius: 6, padding: 0,
                    cursor: bgColorEnabled ? 'pointer' : 'not-allowed', opacity: bgColorEnabled ? 1 : 0.4,
                    background: 'none',
                  }}
                />
                <span style={{ fontSize: 12, color: '#555', fontFamily: 'Inter, Arial, sans-serif', fontVariantNumeric: 'tabular-nums', opacity: bgColorEnabled ? 1 : 0.4 }}>
                  {bgColor}
                </span>
              </div>

              <label style={sectionLabelStyle}>Debug</label>
              <span style={captionStyle}>{"Live performance stats over the scene."}</span>
              <button
                type="button"
                onClick={toggleDebugHud}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  background: 'none', border: 'none', padding: '2px 0 16px', cursor: 'pointer',
                  fontFamily: 'Inter, Arial, sans-serif',
                }}
              >
                <span style={{
                  width: 34, height: 20, borderRadius: 10, flexShrink: 0, position: 'relative',
                  background: debugHud ? '#1a1a1a' : '#ccc', transition: 'background 0.15s',
                }}>
                  <span style={{
                    position: 'absolute', top: 2, left: debugHud ? 16 : 2, width: 16, height: 16,
                    borderRadius: '50%', background: '#fff', transition: 'left 0.15s',
                  }} />
                </span>
                <span style={{ fontSize: 13, color: '#1a1a1a' }}>DEBUG Performance</span>
              </button>
          </>
        )}

        </div>

        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={closeModal} style={{ ...modalSecondaryBtn, flex: 1 }}>
            {isStreaming ? 'Close' : 'Cancel'}
          </button>
          <button type="button" onClick={handleApply} style={{ ...modalPrimaryBtn, flex: 1 }}>
            {isStreaming ? ((sh || '') !== (readParam('sh') || '') ? 'Apply & reload' : 'Save') : 'Apply'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default QualityModal;
